import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Check, CreditCard, FileText, Building2, Zap, ArrowLeft, Users } from 'lucide-react';

const BillingPage = () => {
  const [selectedTier, setSelectedTier] = useState('pro');
  const [contractLength, setContractLength] = useState('12');
  const [employeeCount, setEmployeeCount] = useState(5);
  const [selectedMethod, setSelectedMethod] = useState('stripe_checkout');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const tiers = [
    {
      id: 'start',
      name: 'Start',
      basePrice: 1990,
      perEmployee: 199,
      desc: 'Grundpaketet för entreprenörer',
      features: ['Jobbkalkyl & material', 'Offert & PDF + e-post', 'Projekthantering', 'Kalender & påminnelser'],
    },
    {
      id: 'pro',
      name: 'Pro',
      basePrice: 2990,
      perEmployee: 149,
      desc: 'Mest populär — allt du behöver',
      highlighted: true,
      features: ['Allt i Start', 'Fakturering', 'Tidrapportering (timmar)', 'Maskintimmar', 'ÄTA-hantering'],
    },
    {
      id: 'business',
      name: 'Business',
      basePrice: 4490,
      perEmployee: 99,
      desc: 'Full kontroll & support',
      features: ['Allt i Pro', 'Lönehantering & lönespec', 'Bokföring', 'Full tillgång alla funktioner', 'Prioriterad support'],
    },
  ];

  const contracts = [
    { months: '6', label: '6 månader', multiplier: 1.10, tag: '+10%' },
    { months: '12', label: '12 månader', multiplier: 1.0, tag: 'Standard' },
    { months: '24', label: '24 månader', multiplier: 0.85, tag: '-15%' },
  ];

  const paymentMethods = [
    { id: 'stripe_checkout', name: 'Kort / Apple Pay / Google Pay', icon: Zap, desc: 'Betala direkt', badge: 'Snabbast' },
    { id: 'stripe_card', name: 'Kortbetalning', icon: CreditCard, desc: 'Automatisk månadsbetalning' },
    { id: 'stripe_invoice', name: 'Faktura', icon: FileText, desc: 'E-postfaktura varje period' },
    { id: 'bankgiro', name: 'Bankgiro', icon: Building2, desc: 'Manuell betalning' },
  ];

  const currentTier = tiers.find(t => t.id === selectedTier);
  const currentContract = contracts.find(c => c.months === contractLength);
  const baseMonthly = Math.round(currentTier.basePrice * currentContract.multiplier);
  const employeeCost = currentTier.perEmployee * employeeCount;
  const totalMonthly = baseMonthly + employeeCost;
  const planKey = `${selectedTier}_${contractLength}`;
  const hasTrial = selectedMethod === 'stripe_checkout' || selectedMethod === 'stripe_card';

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      if (selectedMethod === 'stripe_checkout') {
        const response = await api.createCheckoutSession({ plan: planKey, employee_count: employeeCount, origin_url: originUrl });
        if (response.data.url) { window.location.href = response.data.url; return; }
      } else {
        await api.createSubscription({ plan: planKey, employee_count: employeeCount, payment_method: selectedMethod, origin_url: originUrl });
        toast.success('Prenumeration skapad!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte skapa prenumeration');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="billing-page">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center gap-4">
          <Link to="/" className="text-muted-foreground hover:text-foreground"><ArrowLeft size={20} /></Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Välj din plan</h1>
            <p className="text-sm text-muted-foreground mt-0.5">14 dagars gratis testperiod vid kortbetalning</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">

        {/* Contract Length */}
        <div className="flex justify-center">
          <div className="inline-flex bg-card border border-border rounded-lg p-1 gap-1" data-testid="contract-toggle">
            {contracts.map((c) => (
              <button key={c.months} onClick={() => setContractLength(c.months)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${contractLength === c.months ? 'bg-[#1A1A1A] dark:bg-white text-white dark:text-[#1A1A1A]' : 'text-muted-foreground hover:text-foreground'}`}>
                {c.label} <span className={`text-xs ml-1 ${c.multiplier < 1 ? 'text-green-500 font-bold' : c.multiplier > 1 ? 'text-amber-500' : 'text-muted-foreground'}`}>{c.tag}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {tiers.map((tier) => {
            const adjPrice = Math.round(tier.basePrice * currentContract.multiplier);
            const isSelected = selectedTier === tier.id;
            return (
              <div key={tier.id} onClick={() => setSelectedTier(tier.id)}
                className={`relative cursor-pointer rounded-xl p-6 transition-all ${
                  isSelected
                    ? tier.highlighted
                      ? 'bg-[#1A1A1A] dark:bg-[#2A2520] text-white border-2 border-[#1A1A1A] dark:border-[#2A2520] ring-4 ring-[#1A1A1A]/10 dark:ring-white/10 scale-[1.02]'
                      : 'bg-card border-2 border-[hsl(16,100%,55%)] ring-4 ring-[hsl(16,100%,55%)]/10 scale-[1.02]'
                    : tier.highlighted
                      ? 'bg-[#1A1A1A] dark:bg-[#2A2520] text-white border-2 border-[#1A1A1A] dark:border-[#2A2520]'
                      : 'bg-card border border-border hover:border-muted-foreground/30'
                }`}
                data-testid={`tier-${tier.id}`}
              >
                {tier.highlighted && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[hsl(16,100%,55%)] text-white text-xs font-bold px-3 py-1 rounded-full">Rekommenderad</span>
                )}
                <p className={`text-sm font-bold uppercase tracking-wider ${isSelected && tier.highlighted ? 'text-white/70' : isSelected ? 'text-[hsl(16,100%,55%)]' : tier.highlighted ? 'text-white/70' : 'text-muted-foreground'}`}>
                  {tier.name}
                </p>
                <div className="flex items-baseline gap-1 mt-2">
                  <span className="text-4xl font-bold">{adjPrice.toLocaleString('sv-SE')}</span>
                  <span className={`text-sm ${tier.highlighted ? 'text-white/50' : 'text-muted-foreground'}`}>kr/mån</span>
                </div>
                <p className={`text-xs mt-1 font-medium ${tier.highlighted ? 'text-white/50' : 'text-muted-foreground'}`}>+{tier.perEmployee} kr/anställd/mån</p>
                <p className={`text-xs mt-0.5 ${tier.highlighted ? 'text-white/60' : 'text-muted-foreground'}`}>{tier.desc}</p>
                <ul className="mt-5 space-y-2">
                  {tier.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <Check size={14} className="text-[hsl(16,100%,55%)] flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Employee Count */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <Users size={20} className="text-[hsl(16,100%,55%)]" />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Antal anställda</h2>
          </div>
          <div className="flex items-center gap-6">
            <input type="range" min="1" max="100" value={employeeCount}
              onChange={(e) => setEmployeeCount(parseInt(e.target.value))}
              className="flex-1 accent-[hsl(16,100%,55%)]"
              data-testid="employee-slider"
            />
            <div className="text-center min-w-[80px]">
              <span className="text-3xl font-bold" data-testid="employee-count">{employeeCount}</span>
              <p className="text-xs text-muted-foreground">anställda</p>
            </div>
            <div className="text-right min-w-[120px]">
              <span className="text-lg font-bold text-[hsl(16,100%,55%)]">{employeeCost.toLocaleString('sv-SE')} kr</span>
              <p className="text-xs text-muted-foreground">/mån anställdkostnad</p>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-card rounded-xl border border-border p-6">
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Betalningsmetod</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {paymentMethods.map((m) => {
              const Icon = m.icon;
              const isActive = selectedMethod === m.id;
              return (
                <div key={m.id} onClick={() => setSelectedMethod(m.id)}
                  className={`relative cursor-pointer rounded-lg p-4 border-2 transition-all ${isActive ? 'border-[hsl(16,100%,55%)] bg-[hsl(16,100%,55%)]/5' : 'border-border hover:border-muted-foreground/30'}`}
                  data-testid={`payment-method-${m.id}`}
                >
                  {m.badge && <span className="absolute -top-2 right-2 bg-[hsl(16,100%,55%)] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{m.badge}</span>}
                  <Icon size={20} className={isActive ? 'text-[hsl(16,100%,55%)]' : 'text-muted-foreground'} />
                  <p className="text-sm font-medium mt-2">{m.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-card rounded-xl border-2 border-[hsl(16,100%,55%)]/20 p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-lg font-bold">{currentTier.name} — {contractLength} mån avtal</p>
              <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                <p>Grundpris: {baseMonthly.toLocaleString('sv-SE')} kr/mån</p>
                <p>Anställda ({employeeCount} st × {currentTier.perEmployee} kr): {employeeCost.toLocaleString('sv-SE')} kr/mån</p>
              </div>
              <p className="text-2xl font-bold mt-2 text-[hsl(16,100%,55%)]">{totalMonthly.toLocaleString('sv-SE')} kr/mån <span className="text-sm font-normal text-muted-foreground">exkl. moms</span></p>
              <p className="text-xs text-muted-foreground mt-1">{hasTrial ? '14 dagars gratis testperiod' : 'Faktureras direkt'}</p>
            </div>
            <Button onClick={handleSubscribe} disabled={loading} data-testid="subscribe-btn"
              className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white px-8 h-12 text-base font-semibold whitespace-nowrap shrink-0">
              {loading ? 'Skapar...' : 'Starta prenumeration'}
            </Button>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground">Säker betalning via Stripe — krypterad anslutning</p>
      </div>
    </div>
  );
};

export default BillingPage;
